import { Close, Print } from '@mui/icons-material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from '@mui/material';
import React, { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { t } from '../constants/translations';
import { useAdmin } from '../contexts/admin.context';
import { useLocale } from '../contexts/locale.context';
import PrimaryButton from './PrimaryButton';

type Props = {
  onClose: () => void;
  onPrint?: () => void;
  isPosPrinter?: boolean;
  autoPrint?: boolean;
  header?: React.ReactNode;
};

const PrintModal = ({ children, onClose, onPrint, isPosPrinter, autoPrint, header }: PropsWithChildren<Props>) => {
  const { lang } = useLocale();
  const divRef = useRef<HTMLDivElement>(null);
  const { cafe } = useAdmin();
  const bottomMargin = Number(cafe?.bill?.bottomMargin) || 0;
  const print = useReactToPrint({ content: () => divRef.current });

  const handlePrint = useCallback(() => {
    print();

    if (onPrint) {
      onPrint();
    }
  }, [onPrint, print]);

  useEffect(() => {
    if (autoPrint) {
      const id = setTimeout(() => {
        handlePrint();
        onClose();
      }, 100);

      return () => {
        clearTimeout(id);
      };
    }

    return () => {};
  }, [autoPrint, handlePrint, onClose]);

  return (
    <Dialog onClose={onClose} open fullWidth maxWidth={isPosPrinter ? 'xs' : 'sm'}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'flex-end', pb: 0, gap: 1 }}>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, '*': { boxSizing: 'border-box' } }}>
        <div>{header}</div>
        <div ref={divRef}>
          <Box sx={{ pb: bottomMargin }}>{children}</Box>
          {!!bottomMargin && <Divider sx={{ borderColor: '#eee' }} />}
        </div>
      </DialogContent>

      <DialogActions>
        <PrimaryButton startIcon={<Print />} variant="contained" onClick={handlePrint}>
          {t.print[lang]}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default PrintModal;
